// src/context/LeagueAwardsContext.jsx
//
// Focused context for league awards derived data.
// Extracts the getLeagueAwards slice from the old DataContext god-hook.
//
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useLeagueData } from './LeagueDataContext';

const LeagueAwardsContext = createContext(null);

/**
 * Hook to access the filtered, position-recalculated league awards.
 * Use this instead of useData() when a component only needs award data.
 */
export const useLeagueAwards = () => {
  const context = useContext(LeagueAwardsContext);
  if (!context) {
    throw new Error('useLeagueAwards must be used within a LeagueAwardsProvider');
  }
  return context;
};

/**
 * Derives league awards from raw data, filtering to paid players only when
 * a paid-players list is present. Provides getLeagueAwards() helper.
 * Depends on LeagueDataProvider being above it in the tree.
 */
export const LeagueAwardsProvider = ({ children }) => {
  const { data } = useLeagueData();

  // Compute filtered awards in one memoised pass.
  // When a paid-players list exists, awards are restricted to those managers
  // and positions are recalculated from the filtered set.
  const leagueAwards = useMemo(() => {
    if (!data) return null;

    // Build paid-players allow-list (empty list means open league — no filtering)
    const paidPlayers = data['Paid Players']
      ? data['Paid Players'].map(player => player.Manager)
      : [];

    if (paidPlayers.length > 0) {
      const filteredAwards = {};

      Object.keys(data.leagueAwards).forEach(category => {
        filteredAwards[category] = data.leagueAwards[category]
          .filter(player => paidPlayers.includes(player.Manager))
          // Recalculate positions after filtering to maintain sequence integrity
          .map((player, index) => ({ ...player, position: index + 1 }));
      });

      return filteredAwards;
    }

    return data.leagueAwards;
  }, [data]);

  // Stable callback wrapper matches the getLeagueAwards() API from the original god-hook
  const getLeagueAwards = useCallback(() => leagueAwards, [leagueAwards]);

  const value = useMemo(() => ({
    leagueAwards,
    getLeagueAwards,
  }), [leagueAwards, getLeagueAwards]);

  return (
    <LeagueAwardsContext.Provider value={value}>
      {children}
    </LeagueAwardsContext.Provider>
  );
};

export default LeagueAwardsContext;
